import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import {
  cloneExecutionColumnOptions,
  cloneExecutionDefaultColumnVisibilityModel,
  cloneExecutionDefaultSortDirection,
  cloneExecutionDefaultSortKey,
  type CloneExecutionSortDirection,
  type CloneExecutionSortKey,
  cloneExecutionSortOptions,
  cloneExecutionStatusOptions,
} from '@/features/suites/pages/suiteExecutionsTableDefinition';
import {
  cloneColumnOptions,
  cloneDefaultColumnVisibilityModel,
  cloneDefaultSortDirection,
  cloneDefaultSortKey,
  type CloneSortDirection,
  type CloneSortKey,
  cloneSortOptions,
  cloneStatusOptions,
} from '@/features/suites/pages/suitesTableDefinition';
import { s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { Panel } from '@/ui/composites/Panel';
import { Box, Tab, Tabs, type GridColumnVisibilityModel } from '@/ui/primitives';
import { SuiteExecutionsTable } from '../components/SuiteExecutionsTable';
import { SuitesTable } from '../components/SuitesTable';
import type { SuiteStatusKey } from '@/features/dashboard/components/dashboardStatusDataHelpers';
import type { TableQueryMeta } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  QueryResult,
  SuiteExecutionDto,
  SuiteExecutionStatus,
  SuiteListItemDto,
} from '@flowqa/shared';
import type { Dayjs } from 'dayjs';
import type { Dispatch, ReactElement, SetStateAction } from 'react';

type CloneSuitesPanelTab = 'suites' | 'executions';

type SuitesPageClonePanelProps = {
  cloneActiveTab: CloneSuitesPanelTab;
  setCloneActiveTab: Dispatch<SetStateAction<CloneSuitesPanelTab>>;
  cloneDateQuickFilter: '7d' | '14d' | '30d' | 'latest';
  setCloneDateQuickFilter: Dispatch<SetStateAction<'7d' | '14d' | '30d' | 'latest'>>;
  cloneFromDate: Dayjs | null;
  setCloneFromDate: Dispatch<SetStateAction<Dayjs | null>>;
  cloneToDate: Dayjs | null;
  setCloneToDate: Dispatch<SetStateAction<Dayjs | null>>;
  cloneColumnVisibilityModel: GridColumnVisibilityModel;
  setCloneColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  cloneSortKey: CloneSortKey;
  setCloneSortKey: Dispatch<SetStateAction<CloneSortKey>>;
  cloneSortDirection: CloneSortDirection;
  setCloneSortDirection: Dispatch<SetStateAction<CloneSortDirection>>;
  cloneFilterStatuses: SuiteExecutionStatus[];
  setCloneFilterStatuses: Dispatch<SetStateAction<SuiteExecutionStatus[]>>;
  cloneFilterSuites: string[];
  setCloneFilterSuites: Dispatch<SetStateAction<string[]>>;
  cloneFilterTags: string[];
  setCloneFilterTags: Dispatch<SetStateAction<string[]>>;
  cloneExecutionColumnVisibilityModel: GridColumnVisibilityModel;
  setCloneExecutionColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  cloneExecutionSortKey: CloneExecutionSortKey;
  setCloneExecutionSortKey: Dispatch<SetStateAction<CloneExecutionSortKey>>;
  cloneExecutionSortDirection: CloneExecutionSortDirection;
  setCloneExecutionSortDirection: Dispatch<SetStateAction<CloneExecutionSortDirection>>;
  cloneExecutionFilterStatuses: SuiteExecutionStatus[];
  setCloneExecutionFilterStatuses: Dispatch<SetStateAction<SuiteExecutionStatus[]>>;
  cloneExecutionFilterTags: string[];
  setCloneExecutionFilterTags: Dispatch<SetStateAction<string[]>>;
  cloneAvailableTags: string[];
  cloneExecutionAvailableTags: string[];
  suiteOptions: Array<{ value: string; label: string }>;
  cloneDateWindow: {
    startDay: string | null;
    endDay: string | null;
    sparklineRangeDays: 7 | 14 | 30;
  };
  cloneStatusFilterKey: string;
  cloneSuiteFilterKey: string;
  cloneTagFilterKey: string;
  cloneExecutionStatusFilterKey: string;
  cloneExecutionTagFilterKey: string;
  cloneNoRowsText: string;
  cloneExecutionsNoRowsText: string;
  debouncedCloneSearch: string;
  debouncedCloneExecutionSearch: string;
  cloneSuitesMeta: TableQueryMeta<SuiteListItemDto> | null;
  setCloneSuitesMeta: Dispatch<SetStateAction<TableQueryMeta<SuiteListItemDto> | null>>;
  cloneSuiteExecutionsMeta: TableQueryMeta<SuiteExecutionDto> | null;
  setCloneSuiteExecutionsMeta: Dispatch<SetStateAction<TableQueryMeta<SuiteExecutionDto> | null>>;
  cloneSuiteExecutionDateBounds: { from: Dayjs | null; to: Dayjs | null };
  showCloneDeltas: boolean;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  handleSearchChange: (value: string) => void;
  handleRangeChange: (value: '7d' | '14d' | '30d' | 'latest') => void;
  handleSelectedStartDayChange: (value: string | null) => void;
  handleSelectedEndDayChange: (value: string | null) => void;
  handleStatusesChange: (value: SuiteStatusKey[]) => void;
  handleTagsChange: (value: string[]) => void;
  handleDebouncedCloneSearchChange: (value: string) => void;
  handleDebouncedCloneExecutionSearchChange: (value: string) => void;
  cloneQueryFn: (input: GridQueryInput) => Promise<QueryResult<SuiteListItemDto>>;
  cloneExecutionQueryFn: (input: GridQueryInput) => Promise<QueryResult<SuiteExecutionDto>>;
};

export const SuitesPageClonePanel = ({
  cloneActiveTab,
  setCloneActiveTab,
  cloneDateQuickFilter,
  setCloneDateQuickFilter,
  cloneFromDate,
  setCloneFromDate,
  cloneToDate,
  setCloneToDate,
  cloneColumnVisibilityModel,
  setCloneColumnVisibilityModel,
  cloneSortKey,
  setCloneSortKey,
  cloneSortDirection,
  setCloneSortDirection,
  cloneFilterStatuses,
  setCloneFilterStatuses,
  cloneFilterSuites,
  setCloneFilterSuites,
  cloneFilterTags,
  setCloneFilterTags,
  cloneExecutionColumnVisibilityModel,
  setCloneExecutionColumnVisibilityModel,
  cloneExecutionSortKey,
  setCloneExecutionSortKey,
  cloneExecutionSortDirection,
  setCloneExecutionSortDirection,
  cloneExecutionFilterStatuses,
  setCloneExecutionFilterStatuses,
  cloneExecutionFilterTags,
  setCloneExecutionFilterTags,
  cloneAvailableTags,
  cloneExecutionAvailableTags,
  suiteOptions,
  cloneDateWindow,
  cloneStatusFilterKey,
  cloneSuiteFilterKey,
  cloneTagFilterKey,
  cloneExecutionStatusFilterKey,
  cloneExecutionTagFilterKey,
  cloneNoRowsText,
  cloneExecutionsNoRowsText,
  debouncedCloneSearch,
  debouncedCloneExecutionSearch,
  cloneSuitesMeta,
  setCloneSuitesMeta,
  cloneSuiteExecutionsMeta,
  setCloneSuiteExecutionsMeta,
  cloneSuiteExecutionDateBounds,
  showCloneDeltas,
  selectedOrganisationId,
  selectedProjectId,
  handleSearchChange,
  handleRangeChange,
  handleSelectedStartDayChange,
  handleSelectedEndDayChange,
  handleStatusesChange,
  handleTagsChange,
  handleDebouncedCloneSearchChange,
  handleDebouncedCloneExecutionSearchChange,
  cloneQueryFn,
  cloneExecutionQueryFn,
}: SuitesPageClonePanelProps): ReactElement => (
  <Panel
    padding="md"
    gap="none"
    contentGap="md"
    headerGap="none"
    headerSlot={
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap="md"
          sx={{
            minWidth: 0,
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
          }}
        >
          <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
            <Tabs
              value={cloneActiveTab}
              onChange={(_event, next) => {
                const nextTab = next as CloneSuitesPanelTab;
                setCloneActiveTab(nextTab);
                if (nextTab === 'suites' && cloneDateQuickFilter === 'latest') {
                  setCloneDateQuickFilter('7d');
                  handleRangeChange('7d');
                }
              }}
              aria-label="Clone suites panel tabs"
              sx={{ pl: s('sm') }}
            >
              <Tab label="Suites" value="suites" />
              <Tab label="Executions" value="executions" />
            </Tabs>
          </Box>
          {cloneActiveTab === 'suites' ? (
            <Box sx={{ flex: '0 0 auto' }}>
              <TableToolbar<CloneSortKey, SuiteExecutionStatus>
                title={undefined}
                searchAriaLabel="Search suites, tags"
                searchValue={debouncedCloneSearch}
                onDebouncedSearchChange={(value) => {
                  handleDebouncedCloneSearchChange(value);
                  handleSearchChange(value);
                }}
                columnVisibilityModel={cloneColumnVisibilityModel}
                defaultColumnVisibilityModel={cloneDefaultColumnVisibilityModel}
                columnOptions={cloneColumnOptions}
                onColumnVisibilityModelChange={setCloneColumnVisibilityModel}
                sortKey={cloneSortKey}
                sortDirection={cloneSortDirection}
                defaultSortKey={cloneDefaultSortKey}
                defaultSortDirection={cloneDefaultSortDirection}
                sortOptions={cloneSortOptions}
                onSortChange={(nextSortKey, nextSortDirection) => {
                  setCloneSortKey(nextSortKey);
                  setCloneSortDirection(nextSortDirection);
                }}
                filterStatuses={cloneFilterStatuses}
                statusOptions={cloneStatusOptions}
                onFilterStatusesChange={(next) => {
                  setCloneFilterStatuses(next);
                  handleStatusesChange(next as SuiteStatusKey[]);
                }}
                filterSuites={cloneFilterSuites}
                onFilterSuitesChange={setCloneFilterSuites}
                suiteOptions={suiteOptions}
                filterTags={cloneFilterTags}
                onFilterTagsChange={(next) => {
                  setCloneFilterTags(next);
                  handleTagsChange(next);
                }}
                dateQuickFilter={cloneDateQuickFilter}
                onDateQuickFilterChange={(next) => {
                  setCloneDateQuickFilter(next);
                  setCloneFromDate(null);
                  setCloneToDate(null);
                  handleRangeChange(next);
                  handleSelectedStartDayChange(null);
                  handleSelectedEndDayChange(null);
                }}
                fromDate={cloneFromDate}
                toDate={cloneToDate}
                onFromDateChange={(next) => {
                  setCloneFromDate(next);
                  handleSelectedStartDayChange(next ? next.format('YYYY-MM-DD') : null);
                }}
                onToDateChange={(next) => {
                  setCloneToDate(next);
                  handleSelectedEndDayChange(next ? next.format('YYYY-MM-DD') : null);
                }}
                availableTags={cloneAvailableTags}
                summaryCount={cloneSuitesMeta?.total ?? null}
                summarySingularLabel="suite"
                summaryPluralLabel="suites"
              />
            </Box>
          ) : null}
          {cloneActiveTab === 'executions' ? (
            <Box sx={{ flex: '0 0 auto' }}>
              <TableToolbar<CloneExecutionSortKey, SuiteExecutionStatus>
                title={undefined}
                searchAriaLabel="Search suite executions"
                searchValue={debouncedCloneExecutionSearch}
                onDebouncedSearchChange={(value) => {
                  handleDebouncedCloneExecutionSearchChange(value);
                  handleSearchChange(value);
                }}
                columnVisibilityModel={cloneExecutionColumnVisibilityModel}
                defaultColumnVisibilityModel={cloneExecutionDefaultColumnVisibilityModel}
                columnOptions={cloneExecutionColumnOptions}
                onColumnVisibilityModelChange={setCloneExecutionColumnVisibilityModel}
                sortKey={cloneExecutionSortKey}
                sortDirection={cloneExecutionSortDirection}
                defaultSortKey={cloneExecutionDefaultSortKey}
                defaultSortDirection={cloneExecutionDefaultSortDirection}
                sortOptions={cloneExecutionSortOptions}
                onSortChange={(nextSortKey, nextSortDirection) => {
                  setCloneExecutionSortKey(nextSortKey);
                  setCloneExecutionSortDirection(nextSortDirection);
                }}
                filterStatuses={cloneExecutionFilterStatuses}
                statusOptions={cloneExecutionStatusOptions}
                onFilterStatusesChange={(next) => {
                  setCloneExecutionFilterStatuses(next);
                  handleStatusesChange(next as SuiteStatusKey[]);
                }}
                filterTags={cloneExecutionFilterTags}
                onFilterTagsChange={(next) => {
                  setCloneExecutionFilterTags(next);
                  handleTagsChange(next);
                }}
                dateQuickFilter={cloneDateQuickFilter}
                onDateQuickFilterChange={(next) => {
                  setCloneDateQuickFilter(next);
                  setCloneFromDate(null);
                  setCloneToDate(null);
                  handleRangeChange(next);
                  handleSelectedStartDayChange(null);
                  handleSelectedEndDayChange(null);
                }}
                fromDate={cloneFromDate}
                toDate={cloneToDate}
                onFromDateChange={(next) => {
                  setCloneFromDate(next);
                  handleSelectedStartDayChange(next ? next.format('YYYY-MM-DD') : null);
                }}
                onToDateChange={(next) => {
                  setCloneToDate(next);
                  handleSelectedEndDayChange(next ? next.format('YYYY-MM-DD') : null);
                }}
                availableTags={cloneExecutionAvailableTags}
                includeLatestQuickFilter
                statusFilterLabel="Status"
                summaryCount={cloneSuiteExecutionsMeta?.total ?? null}
                summarySingularLabel="suite execution"
                summaryPluralLabel="suite executions"
                previewFromDate={
                  cloneDateQuickFilter === 'latest' ? cloneSuiteExecutionDateBounds.from : null
                }
                previewToDate={
                  cloneDateQuickFilter === 'latest' ? cloneSuiteExecutionDateBounds.to : null
                }
              />
            </Box>
          ) : null}
        </Stack>
      </Box>
    }
    sx={{ backgroundColor: 'background.paper' }}
  >
    {cloneActiveTab === 'suites' ? (
      <SuitesTable
        persistKey={null}
        queryKey={[
          'suites-table',
          cloneDateQuickFilter,
          selectedOrganisationId ?? null,
          selectedProjectId ?? null,
          showCloneDeltas ? 'deltas-on' : 'deltas-off',
          cloneSortKey,
          cloneSortDirection,
          cloneStatusFilterKey,
          cloneSuiteFilterKey,
          cloneTagFilterKey,
          cloneDateWindow.startDay,
          cloneDateWindow.endDay,
        ]}
        queryFn={cloneQueryFn}
        pageSizeOptions={[5, 10, 25, 50]}
        initialPageSize={5}
        initialSortModel={[
          {
            field: cloneSortKey,
            sort: cloneSortDirection,
          },
        ]}
        showDeltas={showCloneDeltas}
        noRowsText={cloneNoRowsText}
        showToolbar={false}
        disableColumnSorting
        disableColumnMenu
        search={debouncedCloneSearch}
        onQueryMeta={setCloneSuitesMeta}
        sparklineRangeDays={cloneDateWindow.sparklineRangeDays}
        metricsStartDay={cloneDateWindow.startDay ?? undefined}
        metricsEndDay={cloneDateWindow.endDay ?? undefined}
        columnVisibilityModel={cloneColumnVisibilityModel}
        onColumnVisibilityModelChange={(nextModel) => {
          setCloneColumnVisibilityModel({
            ...nextModel,
            name: true,
          });
        }}
      />
    ) : null}
    {cloneActiveTab === 'executions' ? (
      <SuiteExecutionsTable
        persistKey={null}
        queryKey={[
          'suite-executions-table',
          cloneDateQuickFilter,
          selectedOrganisationId ?? null,
          selectedProjectId ?? null,
          cloneExecutionSortKey,
          cloneExecutionSortDirection,
          cloneExecutionStatusFilterKey,
          cloneExecutionTagFilterKey,
          debouncedCloneExecutionSearch,
          cloneDateWindow.startDay,
          cloneDateWindow.endDay,
        ]}
        queryFn={cloneExecutionQueryFn}
        pageSizeOptions={[5, 10, 25, 50]}
        initialPageSize={5}
        initialSortModel={[
          {
            field: cloneExecutionSortKey,
            sort: cloneExecutionSortDirection,
          },
        ]}
        noRowsText={cloneExecutionsNoRowsText}
        showToolbar={false}
        disableColumnSorting
        disableColumnMenu
        search={debouncedCloneExecutionSearch}
        onQueryMeta={setCloneSuiteExecutionsMeta}
        columnVisibilityModel={cloneExecutionColumnVisibilityModel}
        onColumnVisibilityModelChange={(nextModel) => {
          setCloneExecutionColumnVisibilityModel({
            ...nextModel,
            name: true,
          });
        }}
      />
    ) : null}
  </Panel>
);
